import React from "react";
import { Trash2, Plus } from "lucide-react";
import { TID } from "@/lib/testIds";
import FieldDeltaChip from "@/components/FieldDeltaChip";
import { NPI_Q_DOMAINS, NPI_Q_SEVERITY, NPI_Q_STRESS, computeNpiTotals } from "@/lib/aga";
import StructuredScaleField from "@/components/StructuredScaleField";
import ClockDrawingAttachment from "@/components/ClockDrawingAttachment";
import { SCALE_REGISTRY } from "@/lib/geriatricScales";
import VoiceDictateButton from "@/components/VoiceDictateButton";

// Seções cujos campos são principalmente texto/textarea e, portanto, se
// beneficiam de ditado por voz. Outras (vacinacao com checkboxes, medicamentos
// com tabela, npi_q com tabela) não suportam ditado focado.
const SECTIONS_WITH_DICTATION = new Set([
    "identificacao",
    "antecedentes",
    "social_estilo_vida",
    "funcionalidade",
    "exame_fisico",
    "impressao_conduta",
]);

/**
 * Renders a section of AGA fields based on schema in aga.js.
 * Value is a nested object keyed by field.key. onChange is called with the updated section object.
 * `previous` is the same-shape object from the previous carried-forward consultation
 * (or undefined). When present, each field label shows a small delta chip.
 *
 * `patientId` and `consultationId` are optional but required to enable structured
 * scale pickers (MEEM / GDS-15 / PHQ-9 / MAN / Fried / CFS) and photo attachments.
 */
export default function AGASection({ section, value, onChange, hint, previous, patientId, consultationId }) {
    const set = (key) => (val) => onChange({ ...value, [key]: val });
    const setMany = (patch) => onChange({ ...value, ...patch });

    return (
        <div className="space-y-5">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h3 className="font-display text-xl font-medium text-brand">{section.title}</h3>
                    {section.subtitle && <p className="text-sm text-muted-clinical mt-1">{section.subtitle}</p>}
                    {hint && <p className="text-xs text-[var(--brand-secondary)] mt-2">{hint}</p>}
                </div>
                {SECTIONS_WITH_DICTATION.has(section.id) && (
                    <VoiceDictateButton
                        mode="aga"
                        sectionId={section.id}
                        compact
                        onApply={({ sections }) => {
                            const incoming = (sections && sections[section.id]) || {};
                            if (!Object.keys(incoming).length) return;
                            const next = { ...value };
                            for (const [k, v] of Object.entries(incoming)) {
                                const prev = (next[k] || "").trim();
                                const addition = (v || "").trim();
                                if (!addition) continue;
                                next[k] = prev ? `${prev}\n\n${addition}` : addition;
                            }
                            onChange(next);
                        }}
                    />
                )}
            </div>
            <div className="grid md:grid-cols-2 gap-4">
                {section.fields.map((field) => {
                    // Escalas estruturadas — substituem o input numérico plano
                    if (SCALE_REGISTRY[field.key]) {
                        return (
                            <StructuredScaleField
                                key={field.key}
                                scale={field.key}
                                patientId={patientId}
                                consultationId={consultationId}
                                value={value?.[field.key]}
                                result={value?.[`_scale_${field.key}`]}
                                onSave={(numeric, res) => setMany({
                                    [field.key]: numeric,
                                    [`_scale_${field.key}`]: res,
                                })}
                            />
                        );
                    }
                    // Placeholder do desenho do relógio — renderiza upload dedicado
                    if (field.type === "clock_drawing_photo") {
                        return (
                            <div key={field.key} className="md:col-span-2">
                                <ClockDrawingAttachment
                                    consultationId={consultationId}
                                    patientId={patientId}
                                    kind="clock_drawing"
                                    title={field.label || "Teste do Relógio — foto"}
                                    onAcceptScore={(score, analysis) => setMany({
                                        teste_relogio: score,
                                        _clock_ai_analysis: analysis,
                                    })}
                                />
                            </div>
                        );
                    }
                    return (
                        <FieldRenderer
                            key={field.key}
                            field={field}
                            value={value?.[field.key]}
                            previousValue={previous?.[field.key]}
                            onChange={set(field.key)}
                        />
                    );
                })}
            </div>
        </div>
    );
}

function FieldRenderer({ field, value, previousValue, onChange }) {
    const testid = TID.consultation.field(field.key);
    const delta = <FieldDeltaChip current={value} previous={previousValue} />;

    if (field.type === "textarea") {
        return (
            <div className="md:col-span-2">
                <label className="label-clinical">{field.label}{delta}</label>
                <textarea
                    data-testid={testid}
                    rows={field.rows || 3}
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={field.placeholder}
                    className="input-clinical"
                />
            </div>
        );
    }
    if (field.type === "select") {
        return (
            <div>
                <label className="label-clinical">{field.label}{delta}</label>
                <select data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} className="input-clinical">
                    <option value="">—</option>
                    {field.options.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
            </div>
        );
    }
    if (field.type === "boolean") {
        return (
            <label className="flex items-center gap-2 cursor-pointer md:col-span-2">
                <input data-testid={testid} type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4" />
                <span className="text-sm">{field.label}{delta}</span>
            </label>
        );
    }
    if (field.type === "number") {
        return (
            <div>
                <label className="label-clinical">{field.label}{delta}</label>
                <input
                    data-testid={testid}
                    type="number"
                    min={field.min}
                    max={field.max}
                    step={field.step || 1}
                    value={value ?? ""}
                    onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
                    className="input-clinical"
                />
            </div>
        );
    }
    if (field.type === "checkboxes") {
        const current = Array.isArray(value) ? value : [];
        const toggle = (opt) => {
            if (current.includes(opt)) onChange(current.filter((v) => v !== opt));
            else onChange([...current, opt]);
        };
        return (
            <div className="md:col-span-2">
                <label className="label-clinical">{field.label}</label>
                <div className="flex flex-wrap gap-2">
                    {field.options.map((opt) => {
                        const on = current.includes(opt);
                        return (
                            <button
                                key={opt}
                                type="button"
                                onClick={() => toggle(opt)}
                                data-testid={`${testid}-${opt}`}
                                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${on ? "bg-brand text-white border-brand" : "bg-white text-brand border-[rgba(30,63,51,0.2)]"}`}
                            >
                                {opt}
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    }
    if (field.type === "meds_table") {
        const rows = Array.isArray(value) ? value : [];
        const update = (i, key, v) => {
            const next = [...rows];
            next[i] = { ...(next[i] || {}), [key]: v };
            onChange(next);
        };
        const add = () => onChange([...rows, { name: "", dose: "", route: "", freq: "", indication: "", prescriber: "" }]);
        const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));
        return (
            <div className="md:col-span-2">
                <label className="label-clinical">{field.label}</label>
                <div className="space-y-2">
                    {rows.map((r, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 items-start">
                            <input placeholder="Medicamento" value={r.name || ""} onChange={(e) => update(i, "name", e.target.value)} className="input-clinical col-span-4" data-testid={`med-name-${i}`} />
                            <input placeholder="Dose" value={r.dose || ""} onChange={(e) => update(i, "dose", e.target.value)} className="input-clinical col-span-2" />
                            <input placeholder="Via" value={r.route || ""} onChange={(e) => update(i, "route", e.target.value)} className="input-clinical col-span-1" />
                            <input placeholder="Freq." value={r.freq || ""} onChange={(e) => update(i, "freq", e.target.value)} className="input-clinical col-span-2" />
                            <input placeholder="Indicação" value={r.indication || ""} onChange={(e) => update(i, "indication", e.target.value)} className="input-clinical col-span-2" />
                            <button type="button" onClick={() => remove(i)} className="col-span-1 h-full flex items-center justify-center text-[var(--status-danger)] hover:bg-[var(--status-danger-bg)] rounded" aria-label="remover">
                                <Trash2 size={14} />
                            </button>
                        </div>
                    ))}
                    <button type="button" onClick={add} data-testid={`${testid}-add`} className="btn-secondary text-sm">
                        <Plus size={14} /> Adicionar medicamento
                    </button>
                </div>
            </div>
        );
    }
    if (field.type === "npi_q_table") {
        const data = value && typeof value === "object" ? value : {};
        const totals = computeNpiTotals(data);
        const setItem = (domainKey, patch) => {
            const cur = data[domainKey] || {};
            onChange({ ...data, [domainKey]: { ...cur, ...patch } });
        };
        return (
            <div className="md:col-span-2 space-y-3" data-testid="npi-q-table">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="text-xs text-muted-clinical">
                        {totals.ativos > 0
                            ? <>Sintomas presentes: <strong>{totals.ativos}/12</strong> · Gravidade total: <strong className="text-brand">{totals.gravidade}/36</strong> · Estresse do cuidador: <strong className="text-brand">{totals.estresse}/60</strong></>
                            : "Nenhum sintoma neuropsiquiátrico assinalado ainda."}
                    </div>
                </div>
                <div className="overflow-x-auto -mx-2">
                    <table className="w-full text-sm border-collapse">
                        <thead>
                            <tr className="bg-alt text-left">
                                <th className="p-2 border border-[rgba(30,63,51,0.15)] w-8">Sim</th>
                                <th className="p-2 border border-[rgba(30,63,51,0.15)]">Domínio</th>
                                <th className="p-2 border border-[rgba(30,63,51,0.15)] w-40">Gravidade (paciente)</th>
                                <th className="p-2 border border-[rgba(30,63,51,0.15)] w-44">Estresse do cuidador</th>
                            </tr>
                        </thead>
                        <tbody>
                            {NPI_Q_DOMAINS.map((d) => {
                                const item = data[d.key] || {};
                                return (
                                    <tr key={d.key} data-testid={`npi-row-${d.key}`}>
                                        <td className="p-2 border border-[rgba(30,63,51,0.08)] align-top">
                                            <input
                                                type="checkbox"
                                                data-testid={`npi-${d.key}-present`}
                                                checked={!!item.present}
                                                onChange={(e) => setItem(d.key, { present: e.target.checked })}
                                                className="w-4 h-4"
                                            />
                                        </td>
                                        <td className="p-2 border border-[rgba(30,63,51,0.08)] align-top">
                                            <div className="font-medium text-brand">{d.label}</div>
                                            <div className="text-xs text-muted-clinical">{d.hint}</div>
                                        </td>
                                        <td className="p-2 border border-[rgba(30,63,51,0.08)] align-top">
                                            <select
                                                data-testid={`npi-${d.key}-severity`}
                                                disabled={!item.present}
                                                value={item.gravidade ?? ""}
                                                onChange={(e) => setItem(d.key, { gravidade: e.target.value === "" ? null : Number(e.target.value) })}
                                                className="input-clinical text-xs disabled:opacity-40"
                                            >
                                                <option value="">—</option>
                                                {NPI_Q_SEVERITY.map((o) => (
                                                    <option key={o.value} value={o.value}>{o.label}</option>
                                                ))}
                                            </select>
                                        </td>
                                        <td className="p-2 border border-[rgba(30,63,51,0.08)] align-top">
                                            <select
                                                data-testid={`npi-${d.key}-stress`}
                                                disabled={!item.present}
                                                value={item.estresse ?? ""}
                                                onChange={(e) => setItem(d.key, { estresse: e.target.value === "" ? null : Number(e.target.value) })}
                                                className="input-clinical text-xs disabled:opacity-40"
                                            >
                                                <option value="">—</option>
                                                {NPI_Q_STRESS.map((o) => (
                                                    <option key={o.value} value={o.value}>{o.label}</option>
                                                ))}
                                            </select>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }
    // default: text
    return (
        <div>
            <label className="label-clinical">{field.label}{delta}</label>
            <input data-testid={testid} type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} className="input-clinical" />
        </div>
    );
}
